/**
 * Grouping bars overlay (EXPERIMENTAL — plan §"Grouping bars", flag
 * `mc.groupingBars`). Renders one vertical bar per derived group at the
 * column edge with a rotated label (Heim business name / patient last name).
 *
 * Interactions reuse the existing machinery — no new mutation paths:
 *  - Click  = programmatic multi-select of the group's movable members.
 *  - Drag   = select + pickUp() + previewAtCell() + place() on release,
 *             i.e. exactly a mouse-driven "verschieben" of the block.
 *
 * Rollback = delete this file + model/groupingBars.ts + the flagged render
 * block in MobiliCalendar.tsx.
 */
import React, { useCallback, useMemo, useRef } from "react";
import {
  EMPTY_ID_SET,
  MCCellPos,
  MCGridConfig,
  MCInteractionState,
  MCResource,
} from "../model/types";
import { MCEventIndex } from "../model/eventIndex";
import { buildGroupingBars, MCGroupingBar } from "../model/groupingBars";
import { movableEvents } from "../model/selection";
import { yForMin } from "../model/gridGeometry";
import { MiniStore } from "../model/miniStore";
import { MoveActions } from "../useGridKeyboard";

const BAR_WIDTH_PX = 14;
const BAR_GAP_PX = 2;
const DRAG_THRESHOLD_PX = 5;
/** Below this pixel height the rotated label is dropped (tooltip remains). */
const MIN_LABEL_HEIGHT_PX = 36;

interface GroupingBarsProps {
  cfg: MCGridConfig;
  index: MCEventIndex;
  resources: ReadonlyArray<MCResource>;
  resourceIndexById: ReadonlyMap<string, number>;
  store: MiniStore<MCInteractionState>;
  moveActions: MoveActions;
  cellFromClient: (clientX: number, clientY: number) => MCCellPos | null;
}

interface DragState {
  bar: MCGroupingBar;
  startX: number;
  startY: number;
  dragging: boolean;
}

const GroupingBars: React.FC<GroupingBarsProps> = ({
  cfg,
  index,
  resources,
  resourceIndexById,
  store,
  moveActions,
  cellFromClient,
}) => {
  const bars = useMemo(() => buildGroupingBars(index, resources), [index, resources]);
  const dragRef = useRef<DragState | null>(null);
  const rafRef = useRef(0);

  /** Multi-select the bar's movable members (rings appear, 'v' works). */
  const selectMembers = useCallback(
    (bar: MCGroupingBar): string[] => {
      const ids = movableEvents(index, bar.eventIds).map((e) => e.id);
      store.set({
        multiSelectedIds: ids.length > 0 ? new Set(ids) : EMPTY_ID_SET,
        selectedEventId: null,
        focusedEventId: null,
        range: null,
      });
      return ids;
    },
    [index, store],
  );

  const handlePointerDown = useCallback(
    (bar: MCGroupingBar) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || store.get().move) return;
      e.stopPropagation();
      // Capture: our pointermove/up keep firing even once `.is-moving`
      // disables pointer-events on the bars.
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { bar, startX: e.clientX, startY: e.clientY, dragging: false };
    },
    [store],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;

      if (!drag.dragging) {
        const moved =
          Math.abs(e.clientX - drag.startX) + Math.abs(e.clientY - drag.startY);
        if (moved < DRAG_THRESHOLD_PX) return;
        // Drag begins: select members, anchor the grab cell under the
        // pointer, pick the block up.
        const ids = selectMembers(drag.bar);
        if (ids.length === 0) {
          dragRef.current = null;
          return;
        }
        const grabCell = cellFromClient(drag.startX, drag.startY);
        if (grabCell) store.set({ focus: grabCell });
        if (!moveActions.pickUp()) {
          dragRef.current = null;
          return;
        }
        drag.dragging = true;
      }

      const { clientX, clientY } = e;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const cell = cellFromClient(clientX, clientY);
        if (cell) moveActions.previewAtCell(cell);
      });
    },
    [selectMembers, cellFromClient, store, moveActions],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (drag.dragging) {
        e.stopPropagation();
        // Drop ends the gesture: place when valid, otherwise snap back.
        if (!moveActions.place()) moveActions.cancel();
      } else {
        // Plain click: select the group.
        e.stopPropagation();
        selectMembers(drag.bar);
      }
    },
    [moveActions, selectMembers],
  );

  const stopClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (bars.length === 0) return null;

  return (
    <>
      {bars.map((bar) => {
        const colIndex = resourceIndexById.get(bar.resourceId);
        if (colIndex == null) return null;
        const top = yForMin(cfg, bar.startMin);
        const height = Math.max(yForMin(cfg, bar.endMin) - top - 1, cfg.slotHeightPx);
        const offsetPx = 2 + bar.lane * (BAR_WIDTH_PX + BAR_GAP_PX);
        return (
          <div
            key={bar.id}
            className={`mc-grouping-bar is-${bar.kind}`}
            style={
              {
                left: `calc(var(--mc-col-w) * ${colIndex} + ${offsetPx}px)`,
                top,
                height,
                width: BAR_WIDTH_PX,
                "--mc-bar-color": bar.color ?? undefined,
              } as React.CSSProperties
            }
            title={bar.title}
            onPointerDown={handlePointerDown(bar)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onClick={stopClick}
          >
            {height >= MIN_LABEL_HEIGHT_PX && (
              <span className="mc-grouping-bar-label">{bar.label}</span>
            )}
          </div>
        );
      })}
    </>
  );
};

export default React.memo(GroupingBars);
